import { useRouter } from '@tanstack/react-router';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import DataOutConnectorPage from './index.page';

type DataOutHandoffProps = {
  type: string;
};

/** The two spellings of this connector's URL. */
function useHandoffHrefs(type: string) {
  const router = useRouter();
  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/integrations/data_out/$type',
        params: { workspaceId, type },
      }).href,
    buildLegacy: (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/integrations/data_out/$type',
        params: { workspaceId, type },
      }).href,
  });
}

/**
 * The old `/journeys/integrations/data_out/:type` URL. Hydra claims it only to forward to the
 * migrated route, which keeps the Ember-side directory links working until the teardown converts
 * them.
 */
export function JourneysDataOutHandoff({ type }: DataOutHandoffProps) {
  const { migrated } = useHandoffHrefs(type);

  // The rollout flag is gone, so this always forwards. Passed literally rather than made optional
  // on the shared component: a connector that is still flagged must not be able to say it is on the
  // embedded side without supplying the href to hand back to.
  return <JourneysConnectorHandoff isMigrated migratedHref={migrated} />;
}

/**
 * The migrated `/integrations/data_out/:type` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route applied — data_out nests
 * under it, so it inherits both. This page adds no further gate of its own.
 */
export function DataOutConnectorRouteView({ type }: DataOutHandoffProps) {
  const { legacy } = useHandoffHrefs(type);

  return (
    <ConnectorRouteView isMigrated legacyHref={legacy}>
      {(workspaceId) => <DataOutConnectorPage type={type} workspaceId={workspaceId} />}
    </ConnectorRouteView>
  );
}
